import { Link, useParams, Navigate } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { GraduationCap, Briefcase, Users, Plane, Building2, AlertCircle, CheckCircle2, ArrowRight, Award } from "lucide-react";
import TrainingVisaWizard from "@/components/TrainingVisaWizard";
import TrainingVisa407Extras from "@/components/TrainingVisa407Extras";
import AppealsExtras from "@/components/AppealsExtras";
import StudyNetAIPromo from "@/components/StudyNetAIPromo";

export const SERVICES = {
  student: {
    icon: GraduationCap,
    eyebrow: "Subclass 500",
    title: "Student Visa (Subclass 500)",
    subtitle: "Study in Australia full-time at a CRICOS-registered provider — with a clear Genuine Student case and a credible pathway plan.",
    image: "/assets/students.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "What the Subclass 500 lets you do", points: [
        "Stay in Australia for the duration of your CRICOS-registered course — up to 5 years on a single grant.",
        "Bring eligible family members (partner, dependent children) as secondary applicants.",
        "Work up to 48 hours per fortnight during course sessions, and unlimited hours during scheduled course breaks.",
        "Travel in and out of Australia while the visa is valid.",
        "Live and study in any state or territory in Australia.",
      ]},
      { title: "Who can apply", points: [
        "Applicants enrolled in a CRICOS-registered course (English Language Intensive — ELICOS, VET, Higher Education, Postgraduate Research, Foundation, Non-award, Schools, Defence-sponsored).",
        "Both onshore and offshore applicants — different conditions apply at application time and on the bridging visa.",
        "Applicants must hold a valid Confirmation of Enrolment (CoE) at the time of application.",
      ]},
      { title: "Core eligibility criteria", points: [
        "Genuine Student (GS) requirement — replaced the older GTE from 23 March 2024. Assessed via specific GS questions, your study plan, ties to home country, and intended progression.",
        "Financial capacity — from 10 May 2024: AUD 29,710 for the primary applicant, AUD 10,394 for a partner, AUD 4,449 per dependent child, AUD 13,502 per school-aged child per year; OR personal annual income evidence (AUD 87,856 single / AUD 102,500 with family).",
        "English language requirement — IELTS / PTE / TOEFL iBT / CAE / OET scores vary by course type, or an acceptable exemption.",
        "Overseas Student Health Cover (OSHC) for the entire visa duration — single, couple or family policy from a recognised provider.",
        "Health requirement — health examinations where required by your country grade or course type (commonly medical, chest X-ray).",
        "Character requirement — police clearances where required, declaration of any past visa refusals or character concerns.",
      ]},
      { title: "Subclass 500 visa conditions", points: [
        "Condition 8202 — maintain enrolment and satisfactory attendance and course progress.",
        "Condition 8501 — maintain adequate OSHC for all visa holders for the full visa period.",
        "Condition 8516 — continue to satisfy the requirements for the grant of the visa (including financial capacity).",
        "Condition 8533 — notify your provider of your residential address within 7 days of arrival or change.",
        "Condition 8105 — work limitation (48 hours per fortnight during sessions; unlimited during scheduled breaks).",
        "Condition 8538 — for school-aged minor applicants on Subclass 500.",
      ]},
      { title: "Documents typically required", points: [
        "Valid passport, passport-style photo, identity documents.",
        "Confirmation of Enrolment (CoE) for each course on your visa application.",
        "Genuine Student responses, study plan, statement of purpose and supporting personal statement.",
        "Evidence of financial capacity — bank statements, fixed deposits, loan letters, scholarship letters; sponsor evidence where applicable.",
        "English language test results or exemption evidence.",
        "OSHC certificate covering the visa period.",
        "Academic transcripts, certificates, English-translated documents.",
        "CV/resume, employment records where relevant.",
        "Family documents — marriage certificate, birth certificates of dependents, custody documents.",
      ]},
      { title: "Indicative processing times", points: [
        "Higher Education sector — generally several weeks for low-risk profiles; significantly longer for higher-risk profiles or when additional information is requested.",
        "VET sector — typically longer than Higher Education, particularly for offshore applicants.",
        "ELICOS / Non-award / Foundation — varies widely; provider and country combinations matter.",
        "Note: processing time depends on country grade (assessment level), document quality, course type, and seasonality (peak intake months).",
        "Always confirm latest published processing times via the Department's Global Visa Processing Times dashboard.",
      ]},
      { title: "Common refusal grounds we mitigate", points: [
        "Weak Genuine Student narrative — vague study plan, no connection between past study and chosen course, unclear career outcome.",
        "Insufficient or unexplained funds — sudden large deposits without documented source.",
        "Course-hopping concerns — multiple downgrades or unrelated course switches.",
        "Sponsor relationship and financial capacity gaps for dependent applicants.",
        "Adverse immigration history — past refusals, overstays, withdrawals or cancellations.",
        "Document inconsistencies — discrepancies across CV, transcripts and statements.",
      ]},
      { title: "Pathways after the Student Visa", points: [
        "Subclass 485 Temporary Graduate Visa — for eligible graduates of Australian higher education and select VET qualifications.",
        "Subclass 482 Skills in Demand Visa — employer-sponsored work pathway.",
        "Subclass 189 / 190 / 491 General Skilled Migration — points-tested pathway after meeting work experience requirements.",
        "Subclass 186 Employer Nomination Scheme — permanent employer-sponsored pathway.",
        "Subclass 858 Global Talent / 482 / others — depending on profile.",
      ]},
      { title: "How we support your Student Visa", points: [
        "Pre-application profile assessment — honest review of GS, finance and pathway risk before you pay any visa fees.",
        "Course and provider selection aligned to your future PR pathway, not just admission ease.",
        "GS responses, study plan and statement of purpose drafted with your story in your own voice.",
        "Financial capacity strategy — sponsor evidence packs, source-of-funds documentation, structured account statements.",
        "Document review, certified translations and final lodgement quality control.",
        "Post-grant compliance support — Condition 8202, OSHC, work-hour rules and reporting requirements.",
      ]},
    ],
    notice: "Information on this page reflects Subclass 500 settings published by the Department of Home Affairs as at early 2026. Visa criteria, fees and processing times change — confirmed at lodgement.",
  },
  graduate: {
    icon: GraduationCap,
    eyebrow: "Subclass 485",
    title: "Temporary Graduate Visa (Subclass 485)",
    subtitle: "Live, work and travel in Australia after you complete an eligible Australian qualification — your launchpad to PR.",
    image: "/assets/graduate.jpg",
    fee: "Subclass 485 visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "What the Subclass 485 lets you do", points: [
        "Live, work and study in Australia temporarily after completing an eligible Australian qualification.",
        "Travel in and out of Australia for the duration of the visa.",
        "Include eligible family members on your application.",
        "Use the time to gain Australian work experience, complete a skills assessment, or transition to a longer-term visa.",
      ]},
      { title: "The two main streams", points: [
        "Post-Higher Education Work Stream — for graduates of CRICOS-registered Bachelor / Master / Doctoral degrees. Duration depends on qualification level (typically 2–3 years for Bachelor / Master by coursework, longer for Master by research / Doctorate, with additional time for selected regional study).",
        "Post-Vocational Education Work Stream — for graduates of select CRICOS-registered VET qualifications linked to occupations in demand. Duration is shorter than the Higher Education stream.",
        "Replacement Stream — limited to specific COVID-era cohorts and certain Hong Kong / British National (Overseas) passport holders. Confirm eligibility carefully.",
      ]},
      { title: "Core eligibility (all streams)", points: [
        "Held a valid Subclass 500 (or eligible substantive visa) within the last 6 months at the time of application.",
        "Met the Australian Study Requirement — at least 2 academic years (92 weeks of CRICOS-registered course/s) completed in Australia and finished within the last 6 months.",
        "Under 35 years of age at the time of application (some exemptions and higher caps apply for selected Master by research, Doctorate, and protected categories).",
        "Competent English — IELTS 6.0 overall with no band below 5.0 (or PTE / TOEFL iBT / OET / CAE equivalents).",
        "Adequate health insurance for the visa duration.",
        "Health and character requirements satisfied.",
      ]},
      { title: "Post-Vocational Education Work Stream — extra requirements", points: [
        "Qualification linked to an occupation on the relevant Skilled Occupation List (lists are reviewed by the Department).",
        "Positive skills assessment from the relevant assessing authority (e.g. TRA, VETASSESS) for the nominated occupation.",
        "Trade qualifications must be in scope (e.g. select Cert III / IV / Diploma trades).",
      ]},
      { title: "Indicative visa periods", points: [
        "Bachelor (incl. Honours) — typically 2 years.",
        "Master by Coursework — typically 2 years.",
        "Master by Research / Doctorate — typically 3–4 years.",
        "Eligible VET graduates — typically 18 months.",
        "Regional study bonus — additional 1–2 years for second-tier and third-tier regional study (subject to current policy settings).",
      ]},
      { title: "Visa conditions to be aware of", points: [
        "Condition 8501 — maintain adequate health insurance.",
        "No work-hour cap during the 485 (unlike the Student Visa) — full-time work permitted.",
        "Bridging arrangements — if you apply onshore from a Student Visa, you'll typically be on a Bridging Visa A while the 485 is being processed.",
        "Travel — you can leave and re-enter Australia while your 485 is valid (subject to travel facility).",
      ]},
      { title: "Why timing is critical", points: [
        "The application must be lodged within 6 months of completing your final qualification.",
        "The qualification must have been listed on your previous Student Visa (or you must have evidence of meeting course requirements).",
        "Some occupations and assessing authorities have backlogs — start your skills assessment well before lodgement.",
        "English test validity periods (typically 3 years) and police clearance validity matter for offshore applicants.",
      ]},
      { title: "Pathway planning during the 485", points: [
        "Use the work-rights window to gain skilled employment matching your nominated occupation.",
        "Begin (or complete) your skills assessment if not already done.",
        "Consider Professional Year programs for accounting / engineering / IT for skills-assessment and EOI points.",
        "Aim for state nomination (Subclass 190 / 491) or employer sponsorship (482 / 186).",
        "Plan your EOI in SkillSelect early — points test optimisation takes months, not weeks.",
      ]},
      { title: "How we support your 485", points: [
        "Eligibility deep-dive — qualification, age, English, health and timing checks.",
        "Skills assessment guidance for the VET stream — choosing the right assessing authority.",
        "Document and evidence pack preparation — academic transcripts, AUSC evidence, English certificates.",
        "Visa application drafting and lodgement, including family member inclusion where eligible.",
        "Strategic PR roadmap — how to use the 485 period to maximise your future skilled migration options.",
      ]},
    ],
    notice: "Subclass 485 settings change with each government policy review. Always confirm current age limits, stream eligibility and occupation list with the Department of Home Affairs at the time of lodgement.",
  },
  skilled: {
    icon: Briefcase,
    eyebrow: "Subclass 189 · 190 · 491",
    title: "Skilled Migration",
    subtitle: "Independent, state-nominated and regional skilled visa strategies via SkillSelect.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Please refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Subclass 189 — Skilled Independent", points: ["Permanent visa, no sponsorship required", "Points-tested, invitation only", "Occupation must be on the relevant list"] },
      { title: "Subclass 190 — State Nominated", points: ["Permanent, requires state/territory nomination", "Additional 5 points", "State occupation lists and conditions vary"] },
      { title: "Subclass 491 — Regional (Provisional)", points: ["5-year provisional, regional", "Pathway to subclass 191 PR", "State or family sponsorship"] },
      { title: "Our support", points: ["EOI and SkillSelect submission", "Occupation list checking", "Skills assessment guidance", "Points test review and optimisation"] },
    ],
  },
  employer: {
    icon: Building2,
    eyebrow: "Subclass 482 · 186 · 494",
    title: "Employer Sponsored Visas",
    subtitle: "Skills in Demand, ENS and regional employer sponsorship pathways.",
    image: "/assets/employer.jpg",
    fee: "Visa application charges are subject to change. Please refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Subclass 482 — Skills in Demand (SID)", points: ["Replaced TSS in late 2024", "Core Skills, Specialist Skills and Essential Skills streams", "Employer SBS, nomination and visa application"] },
      { title: "Subclass 186 — Employer Nomination Scheme", points: ["Direct Entry and Temporary Residence Transition streams", "Permanent residence", "Skill, age and English requirements"] },
      { title: "Subclass 494 — Regional Employer Sponsored", points: ["5-year provisional, regional", "Pathway to subclass 191 PR", "Regional employer nomination"] },
      { title: "Our support", points: ["Employer & applicant eligibility review", "Nomination and visa lodgement", "Compliance and post-grant guidance"] },
    ],
  },
  family: {
    icon: Users,
    eyebrow: "Partner · Parent · Child",
    title: "Partner & Family Visas",
    subtitle: "Bringing families together with carefully prepared partner, parent and child applications.",
    image: "/assets/family.jpg",
    fee: "Visa application charges are subject to change. Please refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Partner Visas", points: ["Subclass 820/801 onshore", "Subclass 309/100 offshore", "Prospective marriage 300", "Evidence of genuine and continuing relationship"] },
      { title: "Parent Visas", points: ["Contributory and non-contributory streams", "Balance of family test", "Long processing times — planning is critical"] },
      { title: "Child Visas", points: ["Subclass 101 (offshore) and 802 (onshore)", "Dependent child applications", "Adoption visas where applicable"] },
      { title: "Family Sponsorship", points: ["Sponsor obligations and assurance of support", "Documentation and evidence", "Combined family strategy"] },
    ],
  },
  visitor: {
    icon: Plane,
    eyebrow: "Subclass 600 · Tourist · Family Sponsored · Business",
    title: "Visitor Visa (Subclass 600)",
    subtitle: "Short-stay visits to Australia for tourism, family, business or medical reasons — with strong documentation and a genuine-visitor narrative.",
    image: "/assets/visitor.jpg",
    fee: "Government visa application charges for Subclass 600 are subject to change. Please refer to the current Department of Home Affairs pricing at immi.homeaffairs.gov.au/visas/getting-a-visa/fees-and-charges/current-visa-pricing.",
    sections: [
      { title: "Streams under Subclass 600", points: [
        "Tourist Stream — for holidays, sightseeing and visiting family or friends. Apply from inside or outside Australia.",
        "Sponsored Family Stream — when an eligible Australian relative or government official sponsors you; a security bond may apply.",
        "Business Visitor Stream — for short business engagements such as meetings, negotiations or conferences (no paid work for an Australian employer).",
        "Approved Destination Status Stream — for organised tour groups from the People's Republic of China.",
        "Frequent Traveller Stream — long-validity multiple-entry option for eligible PRC passport holders.",
      ]},
      { title: "Eligibility & key requirements", points: [
        "Genuine temporary stay — clear intention to leave Australia at the end of the visit.",
        "Sufficient funds for the visit and return travel.",
        "Strong ties to your home country (employment, family, property, study).",
        "Good health (PIC 4007) and good character (PIC 4001).",
        "Adequate health insurance recommended for older applicants.",
        "Not have outstanding public health debts to the Commonwealth.",
      ]},
      { title: "Stay periods & conditions", points: [
        "Most grants allow 3, 6 or 12 months stay per entry — final period determined by the Department on assessment.",
        "Common conditions include 8101 (No Work) and 8201 (Maximum 3 months study). The Business Visitor stream allows limited business activities only.",
        "Condition 8503 (No Further Stay) is sometimes attached — meaning you cannot apply for most other visas while in Australia.",
      ]},
      { title: "Documents typically required", points: [
        "Valid passport with sufficient validity beyond your intended stay.",
        "Travel itinerary, accommodation and return ticket details.",
        "Bank statements (usually 3–6 months) and evidence of funds.",
        "Employment letter, leave approval, business registration or study enrolment.",
        "Family or sponsor invitation letter (where applicable).",
        "Previous travel history and any past visa refusals disclosed.",
      ]},
      { title: "How we support you", points: [
        "Stream selection — we recommend the most suitable Visitor stream for your purpose and profile.",
        "Document checklist and review — financial, employment, sponsor and travel evidence.",
        "Drafting a clear genuine-visitor narrative and statement of purpose.",
        "Invitation letter and sponsor documentation drafting where required.",
        "Refusal-risk review — especially for previously refused applicants or first-time travellers from higher-risk profiles.",
        "Online lodgement through ImmiAccount and ongoing case management until decision.",
      ]},
      { title: "Processing times (indicative)", points: [
        "Tourist Stream (outside Australia) — most decisions within a few weeks; complex cases take longer.",
        "Sponsored Family Stream — usually slower due to sponsor checks and security bonds.",
        "Apply at least 6–8 weeks ahead of intended travel. Latest published times: www.immi.homeaffairs.gov.au visa processing times.",
      ]},
      { title: "Why use a Registered Migration Agent?", points: [
        "Refusal of a Visitor Visa can affect future applications across all categories.",
        "Our Registered Migration Agents prepare the file with the same care given to skilled and family applications.",
        "Transparent fees and a clear scope before any engagement.",
        "Online or in-person consultations across Sydney, Melbourne and Perth.",
      ]},
    ],
    notice: "Information on this page is based on the current Department of Home Affairs Subclass 600 guidance. Visa rules and fees are subject to change — confirmed at the time of lodgement.",
  },
  appeals: {
    icon: AlertCircle,
    eyebrow: "Refusals · ART · Judicial Review",
    title: "Visa Appeals & Refusals",
    subtitle: "Independent merits review at the Administrative Review Tribunal (ART), Ministerial intervention and Federal Court guidance.",
    image: "/assets/appeals.jpg",
    fee: "Government and tribunal fees are subject to change. Please refer to the current Department of Home Affairs and ART fee schedules.",
    sections: [
      { title: "What happens when a visa is refused", points: [
        "You receive a written decision setting out the reasons for refusal under the Migration Act 1958 and the relevant regulations.",
        "The decision letter states whether you have review rights and the strict deadline (often 21 or 28 days from notification — sometimes as short as 7 days).",
        "Missing the deadline almost always extinguishes your right of review — act immediately.",
      ]},
      { title: "Administrative Review Tribunal (ART)", points: [
        "Independent merits review — the Tribunal can substitute a new decision.",
        "Applies to most visa refusals, sponsorship and nomination decisions, character cancellations and protection matters.",
        "Hearing-based — applicants can present new evidence and explain their circumstances under oath.",
        "Decisions are based on the law as it stands at the time of decision, not the date of the original application.",
      ]},
      { title: "Common refusal grounds we handle", points: [
        "Subclass 500 Genuine Student / GTE refusals.",
        "Sponsorship and nomination refusals (Subclass 482, 186, 494).",
        "Partner Visa refusals — insufficient evidence of a genuine and continuing relationship.",
        "Skilled visa refusals — skills assessment, English, occupation or age issues.",
        "Character refusals under s501 of the Migration Act.",
        "Visa cancellations and Bridging Visa E issues.",
      ]},
      { title: "Ministerial intervention", points: [
        "A discretionary, last-resort pathway after the Tribunal has affirmed a refusal.",
        "The Minister is not obliged to consider any request — high threshold of \"unique or exceptional circumstances\".",
        "Strict guidelines apply; we will only recommend this where the facts genuinely justify it.",
      ]},
      { title: "Judicial review (Federal Circuit & Family Court / Federal Court)", points: [
        "Limited to jurisdictional error — not a re-hearing on the merits.",
        "Strict 35-day filing deadline from notification of the Tribunal decision.",
        "Court can quash the Tribunal decision and remit the matter for re-determination — it cannot grant the visa itself.",
        "Coordinated through a referral to an Australian legal practitioner where required.",
      ]},
      { title: "How we support refusal & ART matters", points: [
        "Refusal letter review and merit assessment — clear written advice on whether to proceed.",
        "ART application lodgement within deadline, including evidence and submission strategy.",
        "Coordination of new supporting evidence (medical, financial, character, relationship).",
        "Hearing preparation — questions, narrative, document bundle and witness coordination.",
        "Post-hearing follow-up, ministerial intervention assessment or referral to a barrister where appropriate.",
      ]},
      { title: "Timing — why early advice matters", points: [
        "Most review deadlines are non-extendable.",
        "Tribunal listings can take 12–24+ months depending on caseload and visa type.",
        "Onshore applicants will usually be granted a Bridging Visa during ART review — its conditions depend on your previous visa.",
        "Strategic decisions made in the first 7 days often shape the entire outcome.",
      ]},
    ],
    notice: "Refusal, ART and judicial-review matters are complex and time-critical. A paid consultation is required before any engagement — we will provide a clear scope and fee estimate before commencing work.",
  },
  humanitarian: {
    icon: Users,
    eyebrow: "Refugee & Humanitarian Programme",
    title: "Refugee & Humanitarian Visas",
    subtitle: "Protection visas, offshore humanitarian visas and global special humanitarian pathways — supported with care, discretion and rigorous case preparation.",
    image: "/assets/appeals.jpg",
    fee: "Visa application charges (where applicable) are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Who the programme is for", points: [
        "People outside Australia who are subject to substantial discrimination or persecution and require resettlement.",
        "People in Australia who engage Australia's protection obligations under the Refugees Convention or complementary protection.",
        "Family members of refugees and humanitarian entrants seeking to be reunited in Australia.",
      ]},
      { title: "Main visa categories", points: [
        "Subclass 866 — Protection Visa (onshore).",
        "Subclass 200 — Refugee Visa (offshore).",
        "Subclass 201 — In-Country Special Humanitarian Visa.",
        "Subclass 202 — Global Special Humanitarian Visa.",
        "Subclass 203 — Emergency Rescue Visa.",
        "Subclass 204 — Woman at Risk Visa.",
      ]},
      { title: "How we support humanitarian matters", points: [
        "Sensitive intake interview by a Registered Migration Agent with full confidentiality.",
        "Detailed personal narrative drafting — the most important evidence in any protection claim.",
        "Country information research and credibility framing.",
        "Coordination with proposers, support organisations and legal practitioners where required.",
        "Appeal and review support if a first-instance decision is unfavourable.",
      ]},
    ],
    notice: "Humanitarian and protection matters involve highly sensitive personal information. We work with strict confidentiality and only engage where we believe we can add genuine value.",
  },
  citizenship: {
    icon: Award,
    eyebrow: "Citizenship by Conferral · Descent",
    title: "Australian Citizenship",
    subtitle: "Citizenship by conferral and by descent — eligibility, test, ceremony and document preparation.",
    image: "/assets/sydney-harbour-sunset.jpg",
    fee: "Citizenship application charges are subject to change. Please refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Citizenship by Conferral", points: ["Residence requirement check (4 years lawful + 12 months PR)", "Good character requirement", "Citizenship test preparation"] },
      { title: "Citizenship by Descent", points: ["Eligibility where one parent was an Australian citizen at the time of birth", "Document collation and certified copies", "Lodgement and processing guidance"] },
      { title: "End-to-end support", points: ["Application drafting and review", "Ceremony scheduling guidance", "Pledge of commitment preparation"] },
    ],
  },
  "student-guardian-590": {
    icon: Users,
    eyebrow: "Subclass 590",
    title: "Student Guardian Visa (Subclass 590)",
    subtitle: "Stay in Australia as the parent, custodian or qualifying relative of an international student under 18 — or older where exceptional needs apply.",
    image: "/assets/students.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Who Subclass 590 is for", points: [
        "Parents, legal custodians, or eligible relatives aged 21 or over (and of good character) who wish to provide care for an international student.",
        "Primarily intended where the student is under 18 — but available for older students where compelling welfare reasons exist (e.g. illness, disability).",
        "Available both onshore (limited circumstances) and offshore.",
      ]},
      { title: "Eligibility requirements", points: [
        "Be a parent, legal custodian or a qualifying relative of the student (sibling, grandparent, aunt/uncle, step-relations, in-laws as defined in the Migration Regulations).",
        "Be at least 21 years of age at the time of application.",
        "Be of good character — police clearances, character declarations.",
        "Have a genuine intention to remain only for the duration of guardianship.",
        "Have sufficient financial capacity for living costs, return travel and any costs related to the student's stay.",
        "Have a welfare arrangement letter or evidence accepted by the education provider.",
        "Meet health requirements — medical examinations may be required.",
        "Have adequate health insurance equivalent to OSHC for the full visa period.",
      ]},
      { title: "Financial capacity", points: [
        "Funds must cover the entire stay — living costs for yourself, return travel, and any costs associated with the student's stay that are not already met.",
        "Indicative annual living costs published by the Department change periodically — confirm the latest figures at lodgement.",
        "Source of funds matters — bank statements, fixed deposits, sponsor evidence and clear ownership are scrutinised.",
        "Acceptable evidence is similar to the Student Visa: regular bank statements, income evidence, business statements, sale of property documentation.",
      ]},
      { title: "Visa conditions", points: [
        "Condition 8101 — no work in Australia.",
        "Condition 8201 — study for no more than 3 months in total during the visa period.",
        "Condition 8501 — maintain adequate health insurance for the entire visa stay.",
        "Condition 8537 — visa ceases 7 days after the student turns 18 or upon a change to welfare arrangements (whichever is earlier), unless a separate guardianship is established.",
        "Cannot bring family members other than dependent children under 6 (and only with strong compassionate and compelling reasons).",
      ]},
      { title: "Documents typically required", points: [
        "Valid passport, recent passport photo and identity documents.",
        "Relationship evidence — birth certificate, family registry, marriage certificate, custody documents where applicable.",
        "Education provider's welfare arrangement letter or evidence of approval.",
        "Student's CoE and evidence of enrolment.",
        "Financial documents — bank statements, fixed deposits, income evidence, sponsor declarations.",
        "Health insurance evidence equivalent to OSHC.",
        "Statement explaining the reason for guardianship and intention to depart on its conclusion.",
        "Translated copies of foreign-language documents.",
      ]},
      { title: "Indicative processing", points: [
        "Processing time is heavily influenced by the strength of the welfare arrangement, financial evidence and any character / health issues raised.",
        "Offshore applications are most common; onshore applications have additional substantive-visa requirements.",
        "Always check the latest Department of Home Affairs published processing times before lodging.",
      ]},
      { title: "How we support Subclass 590 applications", points: [
        "Eligibility assessment — relationship to student, age, financial capacity and intention review.",
        "Coordination with the student's education provider for welfare arrangement letters.",
        "Drafting of statement of purpose and supporting personal statements.",
        "Source-of-funds strategy and document checklist tailored to your country and profile.",
        "Refusal-risk review before lodgement, particularly for previously refused or borderline cases.",
        "Post-grant support — extension planning, condition compliance, and transition planning if the student's circumstances change.",
      ]},
    ],
    notice: "Subclass 590 is welfare-sensitive and individually assessed. Final outcomes depend on the strength of the welfare arrangement, the student's age and circumstances, and current Departmental policy.",
  },
  "training-407": {
    icon: Briefcase,
    eyebrow: "Subclass 407",
    title: "Training Visa (Subclass 407)",
    subtitle: "Up to 24 months of structured workplace training in Australia — to enhance occupational skills, satisfy registration requirements or participate in a capacity-building program.",
    image: "/assets/employer.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "What the Subclass 407 lets you do", points: [
        "Stay in Australia for up to 2 years to undertake the approved training activity.",
        "Bring eligible family members on your application — with the right to work and study while in Australia.",
        "Travel in and out of Australia for the duration of the visa.",
        "Build skills, gain Australian workplace exposure and consider future migration pathways legally.",
      ]},
      { title: "Three training streams", points: [
        "Occupational training required for registration / licensing / professional development in your home country.",
        "Occupational training to enhance skills in an eligible occupation — typically supporting a defined career path.",
        "Capacity-building training endorsed by a Commonwealth, state or territory government agency — often part of bilateral or multilateral programs.",
      ]},
      { title: "Sponsor and nomination", points: [
        "An approved Temporary Activities Sponsor (TAS) must lodge a nomination outlining a structured, supervised training plan.",
        "Eligible sponsors include Australian-operating businesses, Commonwealth/state/territory agencies, and certain overseas-based government and educational entities.",
        "The training plan must specify competencies to be acquired, supervision arrangements, on-the-job and off-the-job training time, and clear learning outcomes with assessment milestones.",
        "The plan must be at least equivalent to a Cert IV / Diploma level in structure, regardless of the formal qualification linkage.",
      ]},
      { title: "Applicant eligibility", points: [
        "At least 18 years old at the time of application.",
        "Functional English — evidence may include an English test, primary/secondary/tertiary education in English, or other accepted equivalents.",
        "Skills, qualifications and work background appropriate to the nominated occupation — typically 12+ months of relevant experience in the last 24 months.",
        "Genuine intention to depart Australia after training (or transition to another visa lawfully).",
        "Adequate health insurance equivalent to OSHC for the entire visa period.",
        "Health and character requirements satisfied for all applicants 18 and over.",
      ]},
      { title: "Conditions and limitations", points: [
        "Work in Australia is limited to the activities described in the approved training plan — no unrelated employment.",
        "Up to 2 years of stay on the first grant.",
        "Family members can be added as secondary applicants.",
        "Both onshore and offshore applications are accepted.",
        "Condition 8501 — maintain adequate health insurance.",
      ]},
      { title: "Documents typically required", points: [
        "Valid passport, photos and identity documents.",
        "Approved nomination from the Temporary Activities Sponsor.",
        "Detailed training plan with competencies, supervision and assessment milestones.",
        "Employment history evidence — references, payslips, contracts demonstrating 12+ months in the nominated occupation.",
        "Qualifications and English language evidence.",
        "Police clearances for all countries lived in 12+ months in the past 10 years.",
        "Health examinations if required for your country / risk profile.",
        "Insurance evidence equivalent to OSHC.",
      ]},
      { title: "Common refusal grounds we mitigate", points: [
        "Weak training plan — generic, lacking measurable outcomes or supervision detail.",
        "Inadequate occupational background — the applicant cannot benefit meaningfully from the training.",
        "Sponsor concerns — financial viability, prior compliance issues, or capacity to deliver training.",
        "Lack of genuine intention — perceived attempt to use the 407 as a back-door work pathway.",
        "Functional English not adequately evidenced.",
      ]},
      { title: "How we support Subclass 407 cases", points: [
        "Sponsor TAS application and renewal strategy.",
        "Detailed training plan drafting in line with current Departmental expectations.",
        "Applicant occupational background review and document strategy.",
        "Lodgement of nomination and visa application in parallel where appropriate.",
        "Post-grant compliance support for the sponsor — record-keeping, monitoring and reporting.",
      ]},
      { title: "Useful official references", points: [
        "Department of Home Affairs Subclass 407 page — immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/training-407",
        "Temporary Activities Sponsor information — immi.homeaffairs.gov.au",
        "Migration Regulations 1994 — for legislative definitions of training streams and sponsor obligations.",
      ]},
    ],
    notice: "Subclass 407 outcomes depend heavily on the quality and credibility of the training plan. We strongly recommend a consultation with our team before any nomination or visa application is lodged.",
  },
  "skills-in-demand-482": {
    icon: Briefcase,
    eyebrow: "Subclass 482",
    title: "Skills in Demand Visa (Subclass 482)",
    subtitle: "The new-generation temporary work visa replacing TSS — three streams matched to Australia's evolving labour market and a clearer pathway to permanent residence.",
    image: "/assets/employer.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "What the Skills in Demand Visa lets you do", points: [
        "Live, work and travel in Australia for up to 4 years (5 years for Hong Kong / Specialist Skills stream where eligible).",
        "Bring eligible family members as secondary applicants — with the right to work and study in Australia.",
        "Build the Australian work experience required for permanent residency via Subclass 186 ENS.",
        "Change employers in the same nominated occupation — subject to new nomination by the next sponsor.",
      ]},
      { title: "Three streams", points: [
        "Specialist Skills Stream — high-paid roles at or above the Specialist Skills Income Threshold; excludes trades, machinery operators and labourers. Designed for occupations supporting innovation, leadership and advanced technical capability.",
        "Core Skills Stream — for occupations on the Core Skills Occupation List (CSOL) earning at or above the Core Skills Income Threshold.",
        "Essential Skills Stream — for lower-paid essential roles in sectors of strategic need, with stronger worker protections and union/government oversight (rolled out progressively).",
      ]},
      { title: "Employer / sponsor requirements", points: [
        "Approved Standard Business Sponsorship (SBS) — typically valid for 5 years on first approval.",
        "Lawfully operating Australian business with a real, genuine and ongoing need for the position.",
        "Nominated occupation must align with the chosen stream and meet the relevant income threshold.",
        "Labour Market Testing — required for Core Skills and Essential Skills streams; advertising rules apply (multiple platforms, minimum 4-week period, recent within 6 months).",
        "Skilling Australians Fund (SAF) levy payable at nomination — varies based on business turnover and visa duration.",
        "Adverse information rules, training contribution and worker protection commitments.",
      ]},
      { title: "Applicant eligibility", points: [
        "Relevant qualifications, skills and work experience for the nominated occupation — typically 1+ year of recent relevant experience for SID (compared to 2 years under former TSS).",
        "English language — IELTS 5.0 overall with at least 5.0 in each band (or equivalent PTE / OET / TOEFL / CAE) for Core Skills; higher for Specialist Skills.",
        "Health and character requirements — health examinations and police clearances as needed.",
        "Not subject to any visa bars (e.g. condition 8503, exclusion periods).",
      ]},
      { title: "Pathway to permanent residency", points: [
        "All Subclass 482 holders can access Subclass 186 ENS — Temporary Residence Transition (TRT) stream.",
        "TRT requires 2 years of work for the nominating employer in the same nominated occupation in Australia.",
        "Time spent on a Subclass 482 counts toward Australian residence for citizenship eligibility.",
        "Strategic decisions matter — occupation alignment, employer continuity and salary growth shape your PR pathway.",
      ]},
      { title: "Family members", points: [
        "Partner (spouse or de facto) and dependent children can be included.",
        "Secondary applicants receive full work and study rights.",
        "Family can be added before grant or post-grant via a subsequent application.",
        "Health and character checks apply to all applicants 18 and over.",
      ]},
      { title: "Three-step application flow", points: [
        "Step 1 — Sponsorship: business applies for and receives SBS approval (if not already in place).",
        "Step 2 — Nomination: business nominates the specific position, including LMT (where required), salary evidence and stream alignment.",
        "Step 3 — Visa application: the applicant lodges, including English, skills, health and character evidence.",
        "Steps can be lodged simultaneously — careful sequencing reduces overall timeline.",
      ]},
      { title: "How we support SID 482 cases", points: [
        "End-to-end employer sponsorship, nomination and visa application strategy.",
        "Occupation list and stream selection — minimising risk and maximising PR potential.",
        "Salary structuring against Annual Market Salary Rate (AMSR) and stream thresholds.",
        "Labour Market Testing advertising compliance and audit-ready documentation.",
        "Family inclusion strategy and visa-grant timing.",
        "Pathway-to-PR planning from day one — bridging the 482 to the 186 TRT pathway.",
      ]},
    ],
    notice: "Skills in Demand rules, occupation lists and income thresholds are reviewed periodically by the Department of Home Affairs. Always confirm current legislative settings at the time of lodgement.",
  },
  "employer-nomination-186": {
    icon: Building2,
    eyebrow: "Subclass 186",
    title: "Employer Nomination Scheme (Subclass 186)",
    subtitle: "Permanent employer-sponsored visa — your direct route to Australian permanent residence through an approved employer.",
    image: "/assets/employer.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "What the ENS 186 lets you do", points: [
        "Live and work in Australia permanently from the day the visa is granted.",
        "Include partner and dependent children as secondary applicants — all receive permanent residence.",
        "Travel in and out of Australia for the duration of your travel facility (typically 5 years).",
        "Access Medicare and other social services available to permanent residents.",
        "Apply for Australian citizenship once the residency requirement is met.",
      ]},
      { title: "Three streams", points: [
        "Direct Entry Stream — for skilled workers who have not held a sponsorship visa (or only briefly) and are nominated for an eligible position from outside Australia, or onshore where eligible.",
        "Temporary Residence Transition Stream — for Subclass 482 / TSS holders who have worked for the nominating employer in the nominated occupation for the required period (currently 2 years).",
        "Labour Agreement Stream — for workers nominated under a specific Labour Agreement negotiated between the employer and the Commonwealth (rare and industry-specific).",
      ]},
      { title: "Employer / sponsor requirements", points: [
        "Lawfully operating Australian business with a genuine, full-time, ongoing need for the position.",
        "Nominated position must align with an occupation eligible for the relevant stream.",
        "Salary at or above the Annual Market Salary Rate (AMSR) for the role, and at or above the relevant income threshold.",
        "Compliance with Australian workplace laws, training obligations and the Skilling Australians Fund (SAF) levy.",
        "Standard Business Sponsorship is not required for ENS — but the business must satisfy nomination criteria.",
      ]},
      { title: "Applicant eligibility", points: [
        "Under 45 years of age at the time of nomination (with limited exemptions — e.g. specific medical practitioners, regional applicants, academic specialists).",
        "Competent English — IELTS 6.0 overall with at least 6.0 in each band (or equivalent), unless exempt.",
        "Direct Entry Stream — at least 3 years of relevant skilled work experience and a positive skills assessment for the nominated occupation.",
        "Temporary Residence Transition Stream — at least 2 years working for the nominating employer in the nominated occupation in Australia (some flexibility for pandemic / extended periods).",
        "Health and character requirements satisfied for all applicants 18 and over.",
      ]},
      { title: "Why choose ENS over a temporary visa?", points: [
        "Permanent residence from grant — no provisional waiting period before PR.",
        "Whole family transitions to PR together, including spouse and dependent children.",
        "Strong employer-employee stability — no renewal cycles tied to short-term visa expiry.",
        "Direct pathway to Australian citizenship after meeting the residence requirement.",
        "Travel facility built in (typically 5 years on first grant) — Resident Return Visa later for ongoing travel.",
      ]},
      { title: "Documents typically required", points: [
        "Valid passport, photos and identity evidence.",
        "Skills assessment for Direct Entry (TRT has different evidentiary requirements).",
        "Employment history evidence — references, payslips, tax/superannuation evidence for TRT.",
        "English language test results within validity (typically 3 years).",
        "Health examinations as required, police clearances for every country lived in 12+ months in the past 10 years.",
        "Family member evidence — marriage / de facto / birth certificates.",
        "Employer nomination documents — financial statements, ATO records, position description, AMSR evidence, organisational chart.",
      ]},
      { title: "How we support ENS 186 applications", points: [
        "Stream-selection strategy — Direct Entry vs Temporary Residence Transition based on your full work history.",
        "Coordinated employer nomination and applicant visa preparation, lodged in parallel where possible.",
        "Skills assessment guidance and assessing authority selection where required.",
        "Salary review against AMSR and the income threshold.",
        "Adverse-information mitigation, including past visa refusals, character issues or work-history gaps.",
        "Post-grant compliance support for the employer and ongoing PR planning for the applicant.",
      ]},
    ],
    notice: "ENS occupation lists, age limits and salary thresholds are updated by the Department of Home Affairs. Confirm the latest legislative instruments before lodging any application.",
  },
  "general-skilled-migration": {
    icon: Award,
    eyebrow: "GSM · 189 · 190 · 491",
    title: "General Skilled Migration",
    subtitle: "Points-tested skilled migration pathways — independent, state-nominated and regional — for workers in occupations Australia needs.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "How General Skilled Migration works", points: [
        "Three core skilled visas: Subclass 189 (Independent), Subclass 190 (State Nominated) and Subclass 491 (Skilled Work Regional, Provisional).",
        "Lodge an Expression of Interest (EOI) through SkillSelect — you are not lodging a visa application, you are signalling availability.",
        "Receive an invitation to apply if your profile matches a Department invitation round (189) or state / territory nomination criteria (190 / 491).",
        "Lodge the visa application within 60 days of invitation, with full evidence.",
        "Minimum 65 points to lodge an EOI — in practice, successful invitations usually require significantly higher scores depending on occupation and stream.",
      ]},
      { title: "The points test — what counts", points: [
        "Age — peaks between 25 and 32, with reduced points before and after that window.",
        "English — Competent (0), Proficient (10), Superior (20). English is the single highest-impact lever for most applicants.",
        "Skilled employment — overseas (up to 15) and Australian (up to 20), with combined cap.",
        "Qualifications — Doctorate (20), Bachelor / Master (15), Diploma / Trade (10).",
        "Australian study requirement (5) — for 2+ years of Australian study.",
        "Regional study (5) — for completing courses in a designated regional area.",
        "Credentialled community language (5) — NAATI accreditation.",
        "Partner skills — partner skilled and competent in English (10), competent English only (5), single applicant bonus (10).",
        "Professional Year (5) — accredited programs in accounting, engineering or IT.",
        "State / territory nomination — +5 (Subclass 190) or +15 (Subclass 491).",
      ]},
      { title: "Subclass 189 — Skilled Independent", points: [
        "Permanent visa from grant — no sponsorship, no employer or state required.",
        "Most competitive stream — invitation rounds reflect occupation ceilings and labour market priorities.",
        "Whole family included; permanent residence for all secondary applicants.",
        "Travel facility 5 years, automatic right to work for both primary and secondary applicants.",
      ]},
      { title: "Subclass 190 — Skilled Nominated", points: [
        "Permanent visa from grant — requires nomination by an Australian state or territory.",
        "+5 points for nomination — typically pushes a borderline EOI into competitive territory.",
        "State / territory occupation lists, qualifications, English and residency requirements differ — strategy is essential.",
        "Commitment to live and work in the nominating state for an initial period (commonly 2 years), confirmed by declaration.",
      ]},
      { title: "Subclass 491 — Skilled Work Regional (Provisional)", points: [
        "5-year provisional visa — must live, work and study in a designated regional area for the duration.",
        "Two pathways to nomination: state / territory nomination, or sponsorship by an eligible relative living in regional Australia.",
        "+15 points for nomination / sponsorship.",
        "Pathway to permanent residence via Subclass 191 after meeting required regional residence and minimum-income requirements.",
        "Whole family included, with full work and study rights for secondary applicants.",
      ]},
      { title: "Skills assessments", points: [
        "Mandatory for almost every GSM applicant — must be positive for the nominated occupation.",
        "Different assessing authorities for different occupations: VETASSESS, ACS, Engineers Australia, CPA Australia, ANMAC, AITSL, TRA, AIQS, AIM, ASMIRT, AMSA, and others.",
        "Each authority has its own document checklist, processing time and validity period (typically 3 years).",
        "Suitability assessment and qualifications assessment are sometimes separate stages — plan accordingly.",
      ]},
      { title: "Step-by-step process", points: [
        "Step 1 — Honest eligibility and points review by a Registered Migration Agent.",
        "Step 2 — Skills assessment from the relevant authority.",
        "Step 3 — English testing — sometimes repeated to lift to Proficient or Superior bands.",
        "Step 4 — Lodge EOI in SkillSelect, with truthful and current details (claims are checked at invitation).",
        "Step 5 — State nomination application (for 190 / 491) — strategy across multiple eligible states.",
        "Step 6 — Visa application within 60 days of invitation, with comprehensive supporting evidence.",
        "Step 7 — Health, character and decision.",
      ]},
      { title: "How we support GSM cases", points: [
        "Realistic points review and improvement strategy — sometimes the right answer is to wait 6 months and lift English first.",
        "Skills assessment pathway selection across multiple potential occupations where applicable.",
        "State nomination strategy across multiple eligible states / territories.",
        "EOI drafting, claim verification and ongoing optimisation until invitation.",
        "Invitation-to-visa lodgement, including family evidence, health and character.",
        "Subclass 191 planning from day one for Subclass 491 holders.",
      ]},
    ],
    notice: "GSM occupation lists, invitation cut-offs and state nomination conditions change frequently. We monitor invitation rounds and adjust strategy accordingly.",
  },
  business: {
    icon: Building2,
    eyebrow: "188 · 132 · 888 · Investor",
    title: "Business Visa Australia",
    subtitle: "Business and investor visa pathways — Business Innovation & Investment, Significant Investor, and Business Talent — for entrepreneurs, investors and high-net-worth individuals.",
    image: "/assets/employer.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Why Australia for business and investment", points: [
        "Stable AAA-rated economy, mature legal and banking systems, transparent regulatory environment.",
        "Strategic gateway to fast-growing Asia-Pacific markets — over 60% of global GDP within a single time zone.",
        "Top-ranked universities, multicultural workforce and strong R&D infrastructure for innovation-led ventures.",
        "Quality of life — globally ranked for health, education, safety, environment and infrastructure.",
        "Clear pathways from temporary investor / business visas to permanent residence and citizenship.",
      ]},
      { title: "Subclass 188 — Business Innovation & Investment (Provisional)", points: [
        "Provisional visa for business owners, investors and entrepreneurs — pathway to permanent residence via Subclass 888.",
        "Requires nomination by an Australian state or territory government — each state has its own priorities and thresholds.",
        "Five-year provisional period (subject to current settings) with specific stream conditions.",
        "Whole family included — partner and dependent children with full work and study rights.",
      ]},
      { title: "Subclass 188 streams", points: [
        "Business Innovation Stream — for established business owners with at least 3 years of ownership and a successful business career. Stream-specific turnover, net business assets and personal & business assets thresholds apply.",
        "Investor Stream — designated investment in a state / territory government bond (typically AUD 2.5 million minimum) held for the provisional period; applicant must demonstrate eligible business or investment experience.",
        "Significant Investor Stream — minimum AUD 5 million complying investment portfolio across approved venture capital, emerging companies and balancing investment categories.",
        "Entrepreneur Stream — for innovators with a funding agreement (currently from approved entities) for an innovative venture in an eligible industry. Available pending current policy settings.",
      ]},
      { title: "Subclass 132 — Business Talent (closed to new applications)", points: [
        "Permanent visa from grant — but the Subclass 132 was closed to new applications from 1 July 2024 under the new Business Innovation and Investment policy direction.",
        "Existing holders retain their visa rights and pathways.",
        "Historical context only — included here for awareness, not as a current pathway.",
      ]},
      { title: "Subclass 888 — Business Innovation & Investment (Permanent)", points: [
        "Permanent residence pathway for Subclass 188 holders who satisfy stream-specific thresholds during the provisional period.",
        "Business Innovation 888 — requires evidence of an active main business, turnover and employment in Australia during the provisional period.",
        "Investor 888 — requires the designated investment to have been maintained for the required period.",
        "Significant Investor 888 — requires the complying investment portfolio to have been held throughout the provisional period.",
        "Whole family transitions to permanent residence together.",
      ]},
      { title: "Strategic considerations", points: [
        "Choice of state / territory nomination is critical — different states prioritise different industries and have different investment minimums.",
        "Source of funds and personal / business asset evidence — heavy scrutiny on legitimate accumulation over time.",
        "Tax-residency timing — coordination with accountants on Australian tax residency, CGT and international tax planning.",
        "Banking, FATCA / CRS reporting and complying investment structures need pre-application setup.",
        "Family members — partner's professional career, children's schooling and tertiary education plans are part of the plan.",
      ]},
      { title: "Process overview", points: [
        "Step 1 — Eligibility and stream-selection consultation with our team.",
        "Step 2 — State / territory nomination application, tailored to the chosen stream and state priorities.",
        "Step 3 — Subclass 188 application with full financial, business and personal evidence.",
        "Step 4 — Travel, settlement and investment / business launch in Australia during the provisional period.",
        "Step 5 — Subclass 888 permanent residence application once stream thresholds are met.",
        "Step 6 — Australian citizenship after meeting the residence requirement.",
      ]},
      { title: "How we support business and investor clients", points: [
        "Stream-selection and state-nomination strategy aligned to your industry, capital and goals.",
        "Business plan and investment plan drafting to state and Department expectations.",
        "Source-of-funds documentation, asset valuation reports and net-asset evidence packs.",
        "Coordination with Australian accountants, tax advisors, lawyers and licensed financial planners.",
        "Banking introduction support — setting up Australian business and personal accounts pre-arrival.",
        "Schooling and settlement planning for the whole family.",
        "Pathway-to-PR (Subclass 888) planning from day one of the provisional visa.",
      ]},
    ],
    notice: "Australian business and investor visa policy is highly dynamic. Investment thresholds, streams and even visa availability can change with each Federal Budget. Confirmation of eligibility requires a personalised paid consultation.",
  },
  "standard-business-sponsorship": {
    icon: Building2,
    eyebrow: "SBS",
    title: "Standard Business Sponsorship",
    subtitle: "The foundational sponsorship approval that lets your Australian business nominate overseas workers under the Skills in Demand (482) and related work-visa programs.",
    image: "/assets/employer.jpg",
    fee: "Sponsorship application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Who needs a Standard Business Sponsorship", points: [
        "Any Australian business that wants to nominate workers on a Subclass 482 Skills in Demand visa.",
        "Businesses bringing in overseas talent that they want to retain through to permanent residence via ENS Subclass 186 (Temporary Residence Transition stream).",
        "Australian and offshore businesses seeking to establish or expand operations in Australia using skilled overseas workers.",
      ]},
      { title: "Eligibility requirements", points: [
        "Lawfully established and operating Australian business — verified through ATO, ASIC and financial records.",
        "No adverse information against the business or its directors / partners.",
        "Genuine training and / or contribution to the Australian labour market.",
        "Commitment to comply with all sponsor obligations under the Migration Act 1958.",
      ]},
      { title: "Sponsor obligations", points: [
        "Cooperate with Department monitoring activities and provide records on request.",
        "Pay sponsored workers at or above the Annual Market Salary Rate and any relevant income thresholds.",
        "Notify the Department of changes (e.g. cessation of employment, restructure, ownership change) within set timeframes.",
        "Provide equivalent terms and conditions to those of comparable Australian workers.",
        "Cover certain return-travel costs, where applicable, when a worker's employment ends.",
      ]},
      { title: "How we support sponsors", points: [
        "Business eligibility review and pre-application document audit.",
        "Sponsorship application drafting and lodgement, including supporting financial and operational evidence.",
        "Renewal strategy at the end of the 5-year approval window.",
        "Compliance health checks, monitoring response support and audit-ready record-keeping.",
      ]},
    ],
    notice: "Standard Business Sponsorship is the legal foundation of every employer-sponsored case. Failures or non-compliance can disqualify a business from future sponsorship — early advice is essential.",
  },
  "labour-agreements": {
    icon: Briefcase,
    eyebrow: "Industry / Designated Area",
    title: "Labour Agreements",
    subtitle: "Negotiated agreements between Australian businesses (or industry bodies) and the Commonwealth allowing skilled overseas workers to be sponsored where standard visa programs cannot meet labour-market needs.",
    image: "/assets/employer.jpg",
    fee: "Departmental fees vary by agreement type and visa stream. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Types of Labour Agreements", points: [
        "Company-Specific Labour Agreements — bespoke arrangements with a single employer.",
        "Industry Labour Agreements — for industries with established workforce-shortage frameworks (e.g. meat processing, on-hire, fishing).",
        "Designated Area Migration Agreements (DAMA) — region-specific agreements administered by designated area representatives.",
        "Project Agreements — for major infrastructure or construction projects with specific time-limited needs.",
        "Global Talent Employer Sponsored — for niche, high-demand occupations.",
      ]},
      { title: "When to consider a Labour Agreement", points: [
        "The occupation you need is not on the relevant Skilled Occupation Lists.",
        "Standard salary thresholds are not viable for the region or industry.",
        "Standard English requirements are impractical for the role.",
        "You need flexibility on age limits or qualifications for genuine business need.",
      ]},
      { title: "How the process works", points: [
        "Stakeholder consultation — Department, unions, industry bodies and state / regional partners.",
        "Drafting of the agreement — including labour-market analysis, occupations, concessions and term.",
        "Internal Department review and decision.",
        "Once endorsed, the business can lodge sponsorships and nominations under the agreement.",
      ]},
      { title: "How we support Labour Agreement matters", points: [
        "Initial feasibility assessment and pathway selection.",
        "Industry data and labour-market evidence collation.",
        "Drafting submissions and stakeholder engagement strategy.",
        "Ongoing visa lodgement and compliance under the approved agreement.",
      ]},
    ],
    notice: "Labour Agreements are negotiated arrangements with significant lead times. Early advice helps you decide if this pathway is the right fit before investing in the negotiation.",
  },
  "skilled-independent-189": {
    icon: Award,
    eyebrow: "Subclass 189",
    title: "Skilled Independent Visa (Subclass 189)",
    subtitle: "Permanent residence for points-tested skilled workers — no employer, no state nomination, no regional restriction.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Key features", points: [
        "Permanent visa from grant — full work and study rights.",
        "No sponsorship required — the most competitive GSM stream.",
        "Whole family included; PR for partner and dependent children.",
        "5-year travel facility; pathway to citizenship after meeting residence requirements.",
      ]},
      { title: "Core eligibility", points: [
        "Occupation on the relevant Skilled Occupation List at lodgement / invitation.",
        "Positive skills assessment from the relevant assessing authority.",
        "Under 45 at invitation.",
        "Competent English (IELTS 6.0 / equivalent) — higher bands give more points.",
        "EOI lodged in SkillSelect with at least 65 points; in practice, successful 189 invitations usually require significantly higher scores.",
      ]},
      { title: "How we support 189 cases", points: [
        "Honest points review and 6–12 month improvement plan.",
        "Skills assessment pathway selection and document support.",
        "EOI optimisation and ongoing strategy until invitation.",
        "Family inclusion, health, character and lodgement quality control.",
      ]},
    ],
    notice: "189 occupation lists and invitation rounds change frequently. We monitor your matter through the entire wait until invitation.",
  },
  "skilled-nominated-190": {
    icon: Award,
    eyebrow: "Subclass 190",
    title: "Skilled Nominated Visa (Subclass 190)",
    subtitle: "Permanent residence with a 5-point state nomination boost — for skilled workers committed to living and working in a sponsoring state or territory.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Key features", points: [
        "Permanent visa from grant; whole family included.",
        "+5 points on nomination by an Australian state or territory.",
        "Commitment to live and work in the nominating state for an initial period (commonly 2 years).",
      ]},
      { title: "Core eligibility", points: [
        "Occupation on the relevant Skilled Occupation List and on the nominating state's list.",
        "Positive skills assessment for the nominated occupation.",
        "Under 45 at invitation.",
        "Competent English.",
        "State-specific work experience, settlement-funds, English and ties requirements.",
      ]},
      { title: "How we support 190 cases", points: [
        "State strategy across multiple eligible states — sequenced applications.",
        "EOI drafting, state nomination application and visa lodgement.",
        "Commitment-to-state planning and post-grant compliance.",
      ]},
    ],
    notice: "State nomination criteria change frequently and are highly competitive. We monitor invitation rounds across all states for the best fit.",
  },
  "skilled-work-regional-491": {
    icon: Award,
    eyebrow: "Subclass 491",
    title: "Skilled Work Regional Visa (Subclass 491)",
    subtitle: "Five-year provisional skilled visa requiring residence in a designated regional area — the strongest current pathway for many candidates.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Key features", points: [
        "5-year provisional visa — live, work and study in a designated regional area for the duration.",
        "+15 points for nomination / sponsorship.",
        "Two pathways: state / territory nomination, or sponsorship by an eligible relative living in regional Australia.",
        "Whole family included with full work and study rights.",
        "Pathway to permanent residence via Subclass 191 after meeting residence and minimum-income requirements.",
      ]},
      { title: "Eligibility highlights", points: [
        "Occupation on the relevant Skilled Occupation List and the nominating state's list.",
        "Positive skills assessment.",
        "Under 45 at invitation.",
        "Competent English.",
        "Genuine intention to live in the designated regional area.",
      ]},
      { title: "Designated regional areas", points: [
        "Designated regional Australia includes all of Australia except Sydney, Melbourne and Brisbane (and adjacent metropolitan postcodes).",
        "Includes large cities like Adelaide, Perth, Hobart, Canberra, Gold Coast and the whole of Tasmania, the Northern Territory and ACT.",
        "Confirm postcode eligibility against the current Department of Home Affairs designated regional area list.",
      ]},
      { title: "How we support 491 cases", points: [
        "Regional strategy — state nomination vs eligible-relative sponsorship.",
        "EOI lodgement, state application, visa application and post-grant compliance.",
        "Path-to-191 planning from day one — taxable income tracking and residence diary.",
      ]},
    ],
    notice: "491 nominations are competitive and state-specific. Move quickly when invitations open — slots can close within days.",
  },
  "skilled-regional-pr-191": {
    icon: Award,
    eyebrow: "Subclass 191",
    title: "Permanent Residence (Skilled Regional) Visa (Subclass 191)",
    subtitle: "The permanent residency stage for Subclass 491 and 494 holders who have lived and worked in regional Australia for the required period.",
    image: "/assets/skilled.jpg",
    fee: "Visa application charges are subject to change. Refer to the current Department of Home Affairs pricing.",
    sections: [
      { title: "Key features", points: [
        "Permanent visa from grant — full work and study rights for the whole family.",
        "Designed as the PR endpoint for 491 / 494 holders who have settled in regional Australia.",
        "Removes the regional-residence condition once 191 is granted.",
      ]},
      { title: "Eligibility", points: [
        "Hold (or have held) a Subclass 491 or Subclass 494 visa.",
        "Lived and worked in a designated regional area for at least 3 years on that provisional visa.",
        "Met the minimum taxable income requirement for at least 3 income years during the qualifying period.",
        "Complied with the conditions of the provisional visa.",
        "Met health and character requirements.",
      ]},
      { title: "How we support 191 cases", points: [
        "Residence and income diary — kept from day one of the 491.",
        "ATO tax return collation and minimum-income evidence.",
        "Final 191 application, including family inclusion and any movement-record reconciliation.",
      ]},
    ],
    notice: "Subclass 191 turns 491/494 provisional time into permanent residence — but only with clean income and residence evidence. Track from the start.",
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES[slug];
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  return (
    <div data-testid={`service-page-${slug}`}>
      <PageHeader eyebrow={service.eyebrow} title={service.title} subtitle={service.subtitle} image={service.image} />

      {slug === "training-407" && <TrainingVisaWizard />}
      {slug === "student" && <StudyNetAIPromo variant="feature" />}

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-4">
                <div className="bg-[#F8F9FA] p-7 border border-slate-200">
                  <Icon className="h-10 w-10 text-[#29324A] mb-4" />
                  <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-2">
                    {service.eyebrow}
                  </div>
                  <h3 className="font-display text-2xl text-[#29324A] mb-4">Speak to a Registered Migration Agent</h3>
                  <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                    Confidential, ethical and personalised guidance from our team of Registered Migration Agents — Sydney, Melbourne &amp; Perth.
                  </p>
                  <Link to="/book-consultation" data-testid={`service-${slug}-book-cta`} className="block bg-[#29324A] hover:bg-[#1f2638] text-white font-semibold py-3 text-center transition-colors btn-shimmer">
                    Book a Consultation
                  </Link>
                  <Link to="/pr-assessment" className="block mt-2.5 border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white font-semibold py-3 text-center transition-colors">
                    Free PR Assessment
                  </Link>
                </div>
                {service.notice && (
                  <Disclaimer>{service.notice}</Disclaimer>
                )}
                {service.fee && (
                  <div data-testid={`service-${slug}-fee`} className="bg-[#29324A] text-white p-6 grain relative">
                    <div className="relative">
                      <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-2">Current Government Application Fee</div>
                      <p className="text-sm leading-relaxed">{service.fee}</p>
                      <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                        Source: Department of Home Affairs current visa pricing. Fees subject to change — confirmed at lodgement.
                      </p>
                      <a href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/fees-and-charges/current-visa-pricing" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-[11px] font-bold tracking-widest uppercase text-[#C43A89] underline">
                        View full pricing →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </aside>

            <div className="lg:col-span-8 space-y-10">
              {service.sections.map((sec, i) => (
                <div key={i} data-testid={`service-section-${i}`} className="border-l-2 border-[#C43A89] pl-7">
                  <h2 className="font-display text-2xl md:text-3xl text-[#29324A] mb-5">{sec.title}</h2>
                  <ul className="space-y-3">
                    {sec.points.map((p, pi) => (
                      <li key={pi} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 className="h-5 w-5 text-[#29324A] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <Disclaimer>
                This page provides general information and refers to the Department of Home Affairs visa categories. It does not replace personalised migration advice. Please book a consultation for case-specific guidance.
              </Disclaimer>
            </div>
          </div>
        </div>
      </section>

      {slug === "training-407" && <TrainingVisa407Extras />}
      {slug === "appeals" && <AppealsExtras />}

      <CTASection />
    </div>
  );
}

export function ServicesIndex() {
  return (
    <div>
      <PageHeader
        eyebrow="Migration Services"
        title="Visa categories we specialise in."
        subtitle="Student, skilled, employer sponsored, family, visitor, and refusal/ART matters — guided by Department of Home Affairs frameworks and personalised to your circumstances."
        image="/assets/services.jpg"
      />
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
            {Object.entries(SERVICES).map(([slug, s]) => (
              <Link
                key={slug}
                to={`/services/${slug}`}
                data-testid={`services-index-card-${slug}`}
                className="bg-white p-8 group relative"
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C43A89] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                <s.icon className="h-9 w-9 text-[#29324A] mb-5" />
                <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-2">
                  {s.eyebrow}
                </div>
                <h3 className="font-display text-2xl text-[#29324A] mb-3">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">{s.subtitle}</p>
                <span className="text-sm font-semibold text-[#29324A] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
